import type { ReactNode } from "react";
import { Button } from "antd";

const numberFormatter = new Intl.NumberFormat("pt-BR");

export interface CertameEditorShellUnsavedBar {
  count: number;
  onCancel: () => void;
  onSave: () => void;
  cancelDisabled?: boolean;
  saveLoading?: boolean;
  saveLabel?: string;
}

export interface CertameEditorShellProps {
  /** Back button + title block above the two-column body. */
  header: ReactNode;
  unsavedBar: CertameEditorShellUnsavedBar;
  dadosDoCertame: ReactNode;
  materiasVinculadas: ReactNode;
  bancas: ReactNode;
  /** Page-specific right-column content (tags panel, duplicate-tag review, …). */
  children: ReactNode;
}

// Shared verbatim so both consuming pages read the same wording for the same
// count, instead of each page re-deriving its own plural rules.
function unsavedChangesLabel(count: number): string {
  if (count === 0) return "Nenhuma alteração pendente";
  return `${numberFormatter.format(count)} alteraç${
    count === 1 ? "ão" : "ões"
  } não salva${count === 1 ? "" : "s"}`;
}

/**
 * Two-column shell shared by the certame editor and the tag-review page
 * (frames `T3W9mp` / `zov73`): a fixed 360px left column, a 20px gap, then a
 * flex-fill right column at normal desktop width. Below the width where both
 * no longer fit side by side, it stacks left column above right column —
 * that fallback has no frame to match, since neither frame defines a narrow
 * viewport for this screen.
 */
export function CertameEditorShell({
  header,
  unsavedBar,
  dadosDoCertame,
  materiasVinculadas,
  bancas,
  children,
}: CertameEditorShellProps) {
  return (
    <div>
      {header}

      <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-start">
        <div className="flex w-full flex-col gap-4 lg:w-[360px] lg:shrink-0">
          {dadosDoCertame}
          {materiasVinculadas}
          {bancas}
        </div>

        <div className="min-w-0 flex-1">{children}</div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-edge pt-4">
        <p className="text-sm text-ink-secondary" aria-live="polite">
          {unsavedChangesLabel(unsavedBar.count)}
        </p>
        <div className="flex gap-2">
          <Button onClick={unsavedBar.onCancel} disabled={unsavedBar.cancelDisabled}>
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={unsavedBar.saveLoading}
            onClick={unsavedBar.onSave}
          >
            {unsavedBar.saveLabel ?? "Salvar alterações"}
          </Button>
        </div>
      </div>
    </div>
  );
}
